import { todayIsoDate } from "@/lib/production-schedule"
import type { ProductionPrintDelivery } from "@/types/domain"

export type PrintDeliveryStatus = "UNSCHEDULED" | "SCHEDULED" | "LATE" | "PARTIAL" | "RECEIVED"

export const printDeliveryStatusLabels: Record<PrintDeliveryStatus, string> = {
  UNSCHEDULED: "Sem data",
  SCHEDULED: "Prevista",
  LATE: "Atrasada",
  PARTIAL: "Parcial",
  RECEIVED: "Recebida",
}

/**
 * Situação de uma entrega. Com data de entrega, ela chegou: parcial se veio menos que a tiragem
 * estimada. Sem data de entrega, uma tiragem recebida conta como parcial (ou recebida, se cobre a
 * estimada); senão a entrega está prevista, atrasada ou sem data.
 */
export function printDeliveryStatus(delivery: ProductionPrintDelivery, today = todayIsoDate()): PrintDeliveryStatus {
  const received = delivery.receivedQuantity ?? 0
  const short = delivery.estimatedQuantity != null && received < delivery.estimatedQuantity
  if (delivery.deliveryDate) return delivery.receivedQuantity != null && short ? "PARTIAL" : "RECEIVED"
  if (received > 0) return short ? "PARTIAL" : "RECEIVED"
  if (!delivery.estimatedDate) return "UNSCHEDULED"
  return delivery.estimatedDate < today ? "LATE" : "SCHEDULED"
}

/** Resumo gravado no item; espelha o cálculo de `set_production_item_print_deliveries` no banco. */
export function summarizePrintDeliveries(deliveries: ProductionPrintDelivery[]) {
  const received = deliveries.filter((delivery) => delivery.receivedQuantity != null)
  const pending = deliveries.filter((delivery) => !delivery.deliveryDate && delivery.estimatedDate).map((delivery) => delivery.estimatedDate!).sort()
  const estimated = deliveries.filter((delivery) => delivery.estimatedDate).map((delivery) => delivery.estimatedDate!).sort()
  const delivered = deliveries.length > 0 && deliveries.every((delivery) => delivery.deliveryDate)
  return {
    receivedQuantity: received.length ? received.reduce((total, delivery) => total + delivery.receivedQuantity!, 0) : null,
    estimatedDeliveryDate: pending[0] ?? estimated.at(-1) ?? null,
    finalDeliveryDate: delivered ? deliveries.map((delivery) => delivery.deliveryDate!).sort().at(-1)! : null,
  }
}

export const sumEstimatedQuantity = (deliveries: ProductionPrintDelivery[]) => deliveries.reduce((total, delivery) => total + (delivery.estimatedQuantity ?? 0), 0)
export const sumReceivedQuantity = (deliveries: ProductionPrintDelivery[]) => deliveries.reduce((total, delivery) => total + (delivery.receivedQuantity ?? 0), 0)

/** Próxima entrega ainda não feita, pela data estimada. */
export function nextPendingPrintDelivery(deliveries: ProductionPrintDelivery[]) {
  return deliveries.filter((delivery) => !delivery.deliveryDate && delivery.estimatedDate).sort((first, second) => first.estimatedDate!.localeCompare(second.estimatedDate!))[0]
}

export function newPrintDelivery(estimatedQuantity: number | null): ProductionPrintDelivery {
  return { id: crypto.randomUUID(), estimatedDate: null, estimatedQuantity, deliveryDate: null, receivedQuantity: null }
}
